import axios from "axios"
import { API_CONFIG, API_ENDPOINTS } from "./config"

export const TOKEN_KEY = "access_token"
export const REFRESH_TOKEN_KEY = "refresh_token"
export const AUTH_COOKIE_KEY = "auth_token"

let isRefreshing = false
let refreshSubscribers: Array<(token: string) => void> = []

function subscribeTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback)
}

function onTokenRefreshed(token: string) {
  for (const callback of refreshSubscribers) {
    callback(token)
  }
  refreshSubscribers = []
}

export const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json"
  }
})

// Request interceptor: add token
api.interceptors.request.use((config) => {
  if (typeof window !== "undefined") {
    const token = localStorage.getItem(TOKEN_KEY)
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
  }
  return config
})

// Response interceptor: handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            resolve(api(originalRequest))
          })
        })
      }

      isRefreshing = true

      try {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
        if (!refreshToken) {
          isRefreshing = false
          clearTokens()
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("session-expired"))
          }
          return Promise.reject(error)
        }

        const response = await axios.post(
          `${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH.REFRESH_TOKEN}`,
          { refreshToken }
        )

        // Response: { success, data: { accessToken, refreshToken } }
        const data = response.data?.data || response.data
        const newAccessToken = data.accessToken || data.token
        const newRefreshToken = data.refreshToken

        if (newAccessToken) {
          setTokens(newAccessToken, newRefreshToken)
          isRefreshing = false
          onTokenRefreshed(newAccessToken)
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
          return api(originalRequest)
        }

        throw new Error("No access token received")
      } catch {
        isRefreshing = false
        refreshSubscribers = []
        clearTokens()
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("session-expired"))
        }
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  }
)

// Token helpers
export function setTokens(token: string, refreshToken?: string): void {
  if (typeof window === "undefined") {
    return
  }
  localStorage.setItem(TOKEN_KEY, token)
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  }
}

export function clearTokens(): void {
  if (typeof window === "undefined") {
    return
  }
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

export function isAuthenticated(): boolean {
  if (typeof window === "undefined") {
    return false
  }
  return localStorage.getItem(TOKEN_KEY) !== null
}
